<script setup lang="ts">
/**
 * Builder palette: the searchable, grouped list of building blocks a designer
 * drags onto its canvas — automation steps, report elements, dashboard
 * widgets, fields. Each tile is a drag source carrying the caller's payload
 * (`dragData(item)` → `{ mime: data }`) and, for keyboard and touch users, a
 * button: click or Enter emits `insert`.
 *
 * Groups come from the items' `group` (in first-seen order, or `groupOrder`)
 * and fold open and shut. `recentKeys` adds a „Zuletzt verwendet“ group on
 * top. `layout` is `list` (icon, label, description), `compact` (icon and
 * label in gapless rows one control high — the dense rail) or `grid` (tiles).
 *
 * Two levels: `sections` (key, label, optional `hint` and `tone`) put a
 * tinted, foldable head above the groups of every item whose `section` names
 * it — family → group, the hint shown under the head. Items of a section
 * without a `group` sit directly under the hint. Items without a (known)
 * section, and their groups, come first.
 *
 * Per item: `tone` (`NxAccentTone`) colours its icon — it falls back to the
 * section's `tone`, then to the default brand chip; in the `compact` layout a
 * toned icon gets a small chip, an untoned one stays bare. `active` marks an
 * item that is already in use (placed on the canvas) — still insertable, the
 * tile shows it and screen readers hear `activeLabel`.
 *
 * The search matches every word of the query against label, description,
 * group, section label and the item's `keywords` (internal keys, topics).
 * The `filter` slot sits under the search field for the consumer's own
 * filter (a kind switch); filtering the `items` is the consumer's part.
 *
 * `collapsedGroups` names what starts folded: a group title, a section key,
 * or `<section key>/<group title>` for a group inside a section.
 */
import ChevronDown from 'lucide-vue-next/dist/esm/icons/chevron-down.js';
import { computed, ref, type Component } from 'vue';
import { NxSearchInput } from '../Inputs';
import { accentVars, type NxAccentTone } from '../Panel/accent';

export type NxPaletteTone = NxAccentTone;

export interface NxPaletteItem {
  key: string;
  label: string;
  /** Group title. Without one the item sits on top of its section (or the list), ungrouped. */
  group?: string;
  /** Key of an entry of `sections` — the outer level above the groups. */
  section?: string;
  icon?: Component;
  /** Icon colour; falls back to the section's tone. */
  tone?: NxPaletteTone;
  description?: string;
  /** Extra search words that are not shown (internal keys, topics, aliases). */
  keywords?: readonly string[];
  disabled?: boolean;
  /** Already in use (placed on the canvas): marked, still insertable. */
  active?: boolean;
}

export interface NxPaletteSection {
  key: string;
  label: string;
  /** Short explanation under the section head, shown while it is open. */
  hint?: string;
  /** Tint of the head and default icon tone of the section's items. */
  tone?: NxPaletteTone;
}

const props = withDefaults(
  defineProps<{
    items: readonly NxPaletteItem[];
    /** Payload set on drag start, per MIME type. Without it tiles are not draggable. */
    dragData?: (item: NxPaletteItem) => Record<string, string>;
    layout?: 'list' | 'compact' | 'grid';
    /** Group titles in the order to show; unknown groups follow in first-seen order. */
    groupOrder?: readonly string[];
    /** Outer level above the groups, in this order (see the items' `section`). */
    sections?: readonly NxPaletteSection[];
    /** Keys of recently used items, newest first — shown as their own group. */
    recentKeys?: readonly string[];
    /** Show the search field. Defaults to on from nine items. */
    searchable?: boolean;
    /** Groups and sections that start folded (title, section key, `section/group`). */
    collapsedGroups?: readonly string[];
    searchLabel?: string;
    recentLabel?: string;
    emptyLabel?: string;
    /** Screen-reader text of an `active` item. */
    activeLabel?: string;
    ariaLabel?: string;
  }>(),
  {
    dragData: undefined,
    layout: 'list',
    groupOrder: () => [],
    sections: () => [],
    recentKeys: () => [],
    searchable: undefined,
    collapsedGroups: () => [],
    searchLabel: 'Bausteine suchen …',
    recentLabel: 'Zuletzt verwendet',
    emptyLabel: 'Kein Baustein gefunden.',
    activeLabel: 'bereits verwendet',
    ariaLabel: 'Bausteine',
  }
);

const emit = defineEmits<{
  insert: [item: NxPaletteItem];
  dragstart: [item: NxPaletteItem, event: DragEvent];
}>();

defineSlots<{
  /** Under the search field: the consumer's own filter (e.g. a kind switch). */
  filter?: () => unknown;
}>();

interface SectionBlock {
  kind: 'section';
  key: string;
  section: NxPaletteSection;
  count: number;
  open: boolean;
}
interface GroupBlock {
  kind: 'group';
  key: string;
  /** Fold key; empty for the ungrouped items, which have no head. */
  fold: string;
  title?: string;
  items: NxPaletteItem[];
  recent?: boolean;
  nested?: boolean;
  open: boolean;
}

const query = ref('');
const folded = ref(new Set(props.collapsedGroups));
const showSearch = computed(() => props.searchable ?? props.items.length >= 9);

const sectionByKey = computed(() => new Map(props.sections.map((s) => [s.key, s])));
const sectionOf = (item: NxPaletteItem) =>
  item.section ? sectionByKey.value.get(item.section) : undefined;
const toneOf = (item: NxPaletteItem) => item.tone ?? sectionOf(item)?.tone;

function matches(item: NxPaletteItem, words: string[]) {
  if (!words.length) return true;
  const hay = [
    item.label,
    item.description ?? '',
    item.group ?? '',
    sectionOf(item)?.label ?? '',
    ...(item.keywords ?? []),
  ]
    .join('\n')
    .toLowerCase();
  return words.every((w) => hay.includes(w));
}

const isOpen = (fold: string) => !!query.value.trim() || !folded.value.has(fold);

/** The groups of `items` in order: ungrouped first, then `groupOrder`, then first-seen. */
function groupsOf(items: NxPaletteItem[], section?: NxPaletteSection): GroupBlock[] {
  const prefix = section ? `${section.key}/` : '';
  const out: GroupBlock[] = [];
  const loose = items.filter((i) => !i.group);
  if (loose.length) {
    out.push({ kind: 'group', key: `items:${prefix}`, fold: '', items: loose, open: true });
  }
  const order = [...props.groupOrder];
  for (const item of items) if (item.group && !order.includes(item.group)) order.push(item.group);
  for (const title of order) {
    const inGroup = items.filter((i) => i.group === title);
    if (!inGroup.length) continue;
    const fold = prefix + title;
    out.push({
      kind: 'group',
      key: `group:${fold}`,
      fold,
      title,
      items: inGroup,
      nested: !!section,
      open: isOpen(fold),
    });
  }
  return out;
}

const blocks = computed(() => {
  const words = query.value.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const shown = props.items.filter((i) => matches(i, words));
  const out: (SectionBlock | GroupBlock)[] = [];
  if (!words.length && props.recentKeys.length) {
    const byKey = new Map(props.items.map((i) => [i.key, i]));
    const recent = props.recentKeys.map((k) => byKey.get(k)).filter((i): i is NxPaletteItem => !!i);
    if (recent.length) {
      const fold = props.recentLabel;
      out.push({
        kind: 'group',
        key: `recent:${fold}`,
        fold,
        title: fold,
        items: recent,
        recent: true,
        open: isOpen(fold),
      });
    }
  }
  out.push(...groupsOf(shown.filter((i) => !sectionOf(i))));
  for (const section of props.sections) {
    const inSection = shown.filter((i) => i.section === section.key);
    if (!inSection.length) continue;
    const open = isOpen(section.key);
    out.push({
      kind: 'section',
      key: `section:${section.key}`,
      section,
      count: inSection.length,
      open,
    });
    if (open) out.push(...groupsOf(inSection, section));
  }
  return out;
});

function toggle(fold: string) {
  const next = new Set(folded.value);
  if (next.has(fold)) next.delete(fold);
  else next.add(fold);
  folded.value = next;
}

function onDragStart(item: NxPaletteItem, event: DragEvent) {
  if (item.disabled || !props.dragData || !event.dataTransfer) {
    event.preventDefault();
    return;
  }
  event.dataTransfer.effectAllowed = 'copy';
  for (const [mime, data] of Object.entries(props.dragData(item))) {
    event.dataTransfer.setData(mime, data);
  }
  emit('dragstart', item, event);
}

function insert(item: NxPaletteItem) {
  if (!item.disabled) emit('insert', item);
}
</script>

<template>
  <nav class="nx-palette" :class="`layout-${props.layout}`" :aria-label="props.ariaLabel">
    <div v-if="showSearch || $slots.filter" class="nx-palette__search">
      <NxSearchInput
        v-if="showSearch"
        v-model="query"
        :placeholder="props.searchLabel"
        :aria-label="props.searchLabel"
        height="s"
      />
      <div v-if="$slots.filter" class="nx-palette__filter">
        <slot name="filter" />
      </div>
    </div>
    <div class="nx-palette__body">
      <template v-for="block in blocks" :key="block.key">
        <div
          v-if="block.kind === 'section'"
          class="nx-palette__section"
          :class="{ 'has-tone': !!block.section.tone }"
          :style="accentVars(block.section.tone, null, '--palette-tone')"
          :data-section="block.section.key"
        >
          <button
            type="button"
            class="nx-palette__section-head"
            :aria-expanded="block.open"
            @click="toggle(block.section.key)"
          >
            <ChevronDown class="nx-palette__chevron nxt-icon-sm" aria-hidden="true" />
            <span class="nx-palette__group-title">{{ block.section.label }}</span>
            <span class="nx-palette__count">{{ block.count }}</span>
          </button>
          <p v-if="block.open && block.section.hint" class="nx-palette__hint">
            {{ block.section.hint }}
          </p>
        </div>
        <section
          v-else
          class="nx-palette__group"
          :class="{ 'is-recent': block.recent, 'is-nested': block.nested }"
        >
          <button
            v-if="block.title"
            type="button"
            class="nx-palette__group-head"
            :aria-expanded="block.open"
            @click="toggle(block.fold)"
          >
            <ChevronDown class="nx-palette__chevron nxt-icon-sm" aria-hidden="true" />
            <span class="nx-palette__group-title">{{ block.title }}</span>
            <span class="nx-palette__count">{{ block.items.length }}</span>
          </button>
          <ul v-show="block.open" class="nx-palette__items" role="list">
            <li v-for="item in block.items" :key="item.key">
              <button
                type="button"
                class="nx-palette__tile"
                :class="{ 'is-active': item.active, 'has-tone': !!toneOf(item) }"
                :style="accentVars(toneOf(item), null, '--palette-tone')"
                :disabled="item.disabled"
                :draggable="!!props.dragData && !item.disabled"
                :title="item.description ?? item.label"
                :data-key="item.key"
                @click="insert(item)"
                @dragstart="onDragStart(item, $event)"
              >
                <span v-if="item.icon" class="nx-palette__icon" aria-hidden="true">
                  <component
                    :is="item.icon"
                    :class="
                      props.layout === 'compact' && toneOf(item) ? 'nxt-icon-sm' : 'nxt-icon-md'
                    "
                  />
                </span>
                <span class="nx-palette__text">
                  <span class="nx-palette__label">{{ item.label }}</span>
                  <span v-if="item.description && props.layout === 'list'" class="nx-palette__desc">
                    {{ item.description }}
                  </span>
                  <span v-if="item.active" class="nx-palette__sr-only">
                    {{ `(${props.activeLabel})` }}
                  </span>
                </span>
              </button>
            </li>
          </ul>
        </section>
      </template>
      <p v-if="!blocks.length" class="nx-palette__empty">{{ props.emptyLabel }}</p>
    </div>
  </nav>
</template>

<style scoped>
.nx-palette {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
}
.nx-palette__search {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-xs);
  border-block-end: var(--nxt-hairline) solid var(--nxt-border);
}
.nx-palette__body {
  flex: 1 1 auto;
  min-height: 0;
  padding: var(--nxt-space-2xs) var(--nxt-space-xs) var(--nxt-space-sm);
  overflow-y: auto;
  overscroll-behavior: contain;
}
.nx-palette__group + .nx-palette__group {
  margin-block-start: var(--nxt-space-2xs);
}
.nx-palette__section:not(:first-child) {
  margin-block-start: var(--nxt-space-xs);
}
.nx-palette__group-head,
.nx-palette__section-head {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  width: 100%;
  padding: var(--nxt-space-xs) var(--nxt-space-2xs);
  border: 0;
  border-radius: var(--nxt-radius-sm);
  background: none;
  color: var(--nxt-fg-muted);
  font: inherit;
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  letter-spacing: 0.04em;
  text-align: start;
  text-transform: uppercase;
  cursor: pointer;
}
.nx-palette__group-head:hover {
  color: var(--nxt-fg);
}
.nx-palette__section-head {
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  background: var(--nxt-neutral-subtle-bg);
  color: var(--nxt-neutral-subtle-fg);
}
.nx-palette__section.has-tone .nx-palette__section-head {
  background: color-mix(in srgb, var(--palette-tone) 10%, transparent);
  color: var(--palette-tone-fg);
}
.nx-palette__group.is-nested .nx-palette__group-head {
  padding-block: var(--nxt-space-2xs);
}
.nx-palette__hint {
  margin: var(--nxt-space-2xs) var(--nxt-space-2xs);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  line-height: var(--nxt-line-height-tight);
}
/* An outline, not a box-shadow: the hover lift and the active tile's brand edge
   are box-shadows, and they would wipe a ring drawn the same way — keyboard
   users lost the focus on the active (used) tile. */
.nx-palette__group-head:focus-visible,
.nx-palette__section-head:focus-visible,
.nx-palette__tile:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: 0;
}
.nx-palette__chevron {
  flex: 0 0 auto;
  transition: transform var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-palette__group-head[aria-expanded='false'] .nx-palette__chevron,
.nx-palette__section-head[aria-expanded='false'] .nx-palette__chevron {
  transform: rotate(-90deg);
}
.nx-palette__group-title {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-palette__count {
  padding: 0 var(--nxt-space-xs);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-neutral-subtle-bg);
  color: var(--nxt-neutral-subtle-fg);
  font-variant-numeric: tabular-nums;
  letter-spacing: 0;
}
.nx-palette__items {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  margin: 0;
  padding: var(--nxt-space-2xs) 0;
  font-size: inherit;
  list-style: none;
}
.layout-grid .nx-palette__items {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(calc(var(--nxt-size-unit) * 5.5), 1fr));
}
.nx-palette__tile {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-sm);
  width: 100%;
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-surface-raised-bg);
  color: var(--nxt-fg);
  font: inherit;
  text-align: start;
  cursor: pointer;
  transition:
    border-color var(--nxt-motion-fast) var(--nxt-ease-out),
    box-shadow var(--nxt-motion-fast) var(--nxt-ease-out),
    transform var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-palette__tile[draggable='true'] {
  cursor: grab;
}
.nx-palette__tile:hover:not(:disabled) {
  border-color: var(--nxt-brand-border);
  box-shadow: var(--nxt-shadow-sm);
  transform: translateY(calc(var(--nxt-hairline) * -1));
}
.nx-palette__tile:active:not(:disabled) {
  transform: none;
}
.nx-palette__tile:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.layout-compact .nx-palette__items {
  gap: 0;
}
.layout-compact .nx-palette__tile {
  min-height: var(--nxt-control-size-sm);
  padding: 0 var(--nxt-space-xs);
  border-color: transparent;
  background: none;
}
.layout-compact .nx-palette__tile:hover:not(:disabled) {
  background: var(--nxt-surface-sunken-bg);
  box-shadow: none;
}
.layout-grid .nx-palette__tile {
  flex-direction: column;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-sm) var(--nxt-space-xs);
  text-align: center;
}
.nx-palette__icon {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  width: var(--nxt-control-size-sm);
  height: var(--nxt-control-size-sm);
  border-radius: var(--nxt-radius-sm);
  background: var(--nxt-brand-subtle-bg);
  color: var(--nxt-brand-subtle-fg);
}
.layout-compact .nx-palette__icon {
  width: auto;
  height: auto;
  background: none;
  color: var(--nxt-fg-subtle);
}
.nx-palette__tile.has-tone .nx-palette__icon {
  background: color-mix(in srgb, var(--palette-tone) 10%, transparent);
  color: var(--palette-tone);
}
.layout-compact .nx-palette__tile.has-tone .nx-palette__icon {
  padding: var(--nxt-space-2xs);
}
.nx-palette__tile.is-active,
.nx-palette__tile.is-active:hover:not(:disabled) {
  background: color-mix(in srgb, var(--nxt-brand-bg) 8%, transparent);
  box-shadow: inset calc(var(--nxt-hairline) * 2) 0 0 var(--nxt-brand-bg);
}
.nx-palette__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: var(--nxt-line-height-tight);
}
.nx-palette__label {
  overflow: hidden;
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-medium);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.layout-grid .nx-palette__label {
  font-size: var(--nxt-font-size-xs);
  white-space: normal;
}
.nx-palette__desc {
  margin-block-start: var(--nxt-space-2xs);
  overflow: hidden;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-palette__sr-only {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  margin: calc(var(--nxt-hairline) * -1);
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
.nx-palette__empty {
  margin: 0;
  padding: var(--nxt-space-lg) var(--nxt-space-sm);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
  text-align: center;
}
@media (prefers-reduced-motion: reduce) {
  .nx-palette__tile,
  .nx-palette__chevron {
    transition: none;
  }
  .nx-palette__tile:hover:not(:disabled) {
    transform: none;
  }
}
/* The same where a subtree asks for less motion: data-nx-motion="reduced"
   on an ancestor (reset.css does the durations). */
:where([data-nx-motion='reduced']) .nx-palette__tile,
:where([data-nx-motion='reduced']) .nx-palette__chevron {
  transition: none;
}
:where([data-nx-motion='reduced']) .nx-palette__tile:hover:not(:disabled) {
  transform: none;
}
</style>
